import React, { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Typography
} from "@material-ui/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faTimesCircle } from "@fortawesome/free-solid-svg-icons";

import get from "lodash/get";

import API from "../../../../api";
import SpinnerButton from "../../../../components/buttons/SpinnerButton";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "../../../../components/Dialog";
import colors from "../../../../theme/colors";

interface Preview {
  contract_type: string;
  is_self_finance: boolean;
  is_wells_fargo: boolean;
  refund_amount: number | null;
  checks: {
    withdrawal_completed: boolean;
    carrier_not_paid: boolean;
    not_already_refunded: boolean;
  };
  can_refund: boolean;
  show_close_receivable_toggle: boolean;
}

interface Props {
  open: boolean;
  fundingRequestId: string;
  handleExit: () => void;
  openSnackbar: Function;
}

const CheckRow = ({ ok, label }: { ok: boolean; label: string }): JSX.Element => (
  <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
    <FontAwesomeIcon
      icon={ok ? faCheckCircle : faTimesCircle}
      color={ok ? colors.green : colors.danger}
      style={{ marginRight: 8 }}
    />
    <Typography>{label}</Typography>
  </div>
);

const RefundWalletDialog = ({
  open,
  fundingRequestId,
  handleExit,
  openSnackbar
}: Props): JSX.Element => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [closeReceivable, setCloseReceivable] = useState(false);

  useEffect((): void => {
    if (!open) {
      return;
    }
    setLoading(true);
    setLoadError(false);
    setCloseReceivable(false);
    API.factoring
      .getSelfFinanceWalletRefundPreview(fundingRequestId)
      .then((data: Preview): void => setPreview(data))
      .catch((): void => {
        openSnackbar("error", "Unable to load refund details");
        setPreview(null);
        setLoadError(true);
      })
      .finally((): void => setLoading(false));
  }, [open, fundingRequestId]);

  const handleConfirm = async (): Promise<void> => {
    setSubmitting(true);
    try {
      await API.factoring.refundSelfFinanceWallet(fundingRequestId, closeReceivable);
      openSnackbar("success", "Client wallet refunded and funding request declined");
      handleExit();
    } catch (err) {
      openSnackbar("error", get(err, "response.data.detail") || "Failed to refund client wallet");
    } finally {
      setSubmitting(false);
    }
  };

  const renderBody = (): JSX.Element => {
    if (loading) {
      return <CircularProgress />;
    }
    if (loadError || !preview) {
      return (
        <Typography style={{ color: colors.danger }}>
          Unable to load refund details. Please close and try again.
        </Typography>
      );
    }
    return (
      <React.Fragment>
        {preview.is_wells_fargo && (
          <Typography style={{ color: colors.danger, marginBottom: 12 }}>
            Wells Fargo (ledger) wallet withdrawals cannot be refunded with this action.
          </Typography>
        )}
        <CheckRow ok={preview.checks.withdrawal_completed} label="Wallet withdrawal completed" />
        <CheckRow ok={preview.checks.carrier_not_paid} label="Carrier not paid / no in-flight payout" />
        <CheckRow ok={preview.checks.not_already_refunded} label="Not already refunded" />
        <Typography style={{ marginTop: 12 }}>
          Refund amount:{" "}
          <strong>
            {preview.refund_amount != null ? `$${preview.refund_amount}` : "—"}
          </strong>
        </Typography>
        {preview.show_close_receivable_toggle && (
          <FormControlLabel
            style={{ marginTop: 8 }}
            control={
              <Checkbox
                checked={closeReceivable}
                onChange={(_e, checked): void => setCloseReceivable(checked)}
              />
            }
            label="Close debtor receivable / close contract"
          />
        )}
      </React.Fragment>
    );
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle>Refund client wallet</DialogTitle>
      <DialogContent>{renderBody()}</DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={handleExit}>
          Cancel
        </Button>
        <SpinnerButton
          size="small"
          color="primary"
          onClick={handleConfirm}
          disabled={submitting || loading || !preview?.can_refund}
        >
          Refund wallet
        </SpinnerButton>
      </DialogActions>
    </Dialog>
  );
};

export default RefundWalletDialog;
